import { useState, useCallback, useEffect, useRef } from "react";
import { get3DDesign, save3DDesign, upload3DReference, generate3DDesign, lock3DDesign } from "@/services/locationService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SceneBuilder3D from "@/components/SceneBuilder3D";
import {
  Plus, Trash2, Loader2, Save, Armchair, Lightbulb,
  Palette, Copy, Download, Upload, Lock, Unlock, Image,
  Building, Mountain, ChevronDown, Eye
} from "lucide-react";


const PROP_CATALOG = [
  { type: "table", label: "Table" },
  { type: "chair", label: "Chair" },
  { type: "sofa", label: "Sofa" },
  { type: "bed", label: "Bed" },
  { type: "tv", label: "TV" },
  { type: "shelf", label: "Shelf" },
  { type: "lamp", label: "Lamp" },
  { type: "desk", label: "Desk" },
  { type: "door", label: "Door" },
  { type: "window", label: "Window" },
  { type: "wardrobe", label: "Wardrobe" },
  { type: "rug", label: "Rug" },
  { type: "plant", label: "Plant" },
  { type: "custom", label: "Custom" },
];

const uid = () => Math.random().toString(36).substring(2, 10);

export default function Location3DDesigner({ location, onUpdate }) {
  const [design, setDesign] = useState({
    location_type: location?.location_type || "INT",
    room: { width: 8, depth: 8, height: 3, wall_color: "#e8e0d0", floor_color: "#8b7355" },
    props: [],
    lighting: {
      ambient: { intensity: 0.4, color: "#ffffff" },
      key: { position: { x: 3, y: 4, z: 2 }, intensity: 1.0, color: "#fff5e0" },
      fill: { position: { x: -3, y: 2, z: 2 }, intensity: 0.5, color: "#e0e8ff" },
      back: { position: { x: 0, y: 3, z: -3 }, intensity: 0.3, color: "#ffffff" },
    },
    theme: "",
    reference_uploads: [],
    design_renders: [],
    is_locked: false,
  });
  const [selectedObj, setSelectedObj] = useState(null);
  const [tab, setTab] = useState("props");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [locking, setLocking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [placePropType, setPlacePropType] = useState("chair");
  const [showLightbox, setShowLightbox] = useState(null);
  const fileInputRef = useRef(null);

  // Load existing design
  useEffect(() => {
    const load = async () => {
      try {
        const data = await get3DDesign(location.id);
        if (data) setDesign(prev => ({ ...prev, ...data }));
      } catch { /* no design yet */ }
      finally { setLoading(false); }
    };
    load();
  }, [location.id]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedObj) {
        removeProp(selectedObj.id);
      }
      if (e.key === "Escape") setSelectedObj(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await save3DDesign(location.id, design);
      toast.success("3D design saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const addProp = (type, position) => {
    if (design.is_locked) return toast.error("Design is locked — unlock to edit");
    const pos = position || { x: Math.random() * 3 - 1.5, z: Math.random() * 3 - 1.5 };
    const prop = {
      id: uid(), type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      position: { x: pos.x, y: 0, z: pos.z },
      rotation: { x: 0, y: 0, z: 0 },
    };
    setDesign(prev => ({ ...prev, props: [...prev.props, prop] }));
  };

  const removeProp = (id) => {
    if (design.is_locked) return;
    setDesign(prev => ({ ...prev, props: prev.props.filter(p => p.id !== id) }));
    if (selectedObj?.id === id) setSelectedObj(null);
  };

  const duplicateProp = (prop) => {
    if (design.is_locked) return;
    const newProp = {
      ...prop, id: uid(), label: `${prop.label} copy`,
      position: { x: (prop.position?.x || 0) + 0.5, y: 0, z: (prop.position?.z || 0) + 0.5 },
    };
    setDesign(prev => ({ ...prev, props: [...prev.props, newProp] }));
  };

  const handleLayoutChange = useCallback((change) => {
    setDesign(prev => {
      if (change.type === "prop") {
        return { ...prev, props: prev.props.map(p =>
          p.id === change.id ? { ...p, position: change.position } : p
        )};
      }
      return prev;
    });
  }, []);

  const handleFloorClick = useCallback((pos) => {
    addProp(placePropType, pos);
  }, [placePropType, design.is_locked]); // eslint-disable-line react-hooks/exhaustive-deps

  const captureViewport = () => window.__sceneBuilderCapture ? window.__sceneBuilderCapture() : null;

  // Upload reference image
  const handleUploadRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(",")[1];
        const data = await upload3DReference(location.id, {
          image_data: base64,
          label: file.name.replace(/\.[^/.]+$/, ""),
        });
        setDesign(prev => ({
          ...prev,
          reference_uploads: [...prev.reference_uploads, { id: data.reference.id, label: data.reference.label, image_data: base64 }]
        }));
        toast.success("Reference uploaded");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error("Upload failed");
      setUploading(false);
    }
  };

  // Generate multi-angle renders
  const handleGenerate = async () => {
    if (!design.theme?.trim()) return toast.error("Enter a theme/style description first");
    // Save first
    await handleSave();
    setGenerating(true);
    try {
      const viewport = captureViewport();
      const genData = await generate3DDesign(location.id, {
        theme: design.theme,
        viewport_image: viewport,
      });
      toast.success(genData.message);
      const updated = await get3DDesign(location.id);
      if (updated) setDesign(prev => ({ ...prev, ...updated }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  // Lock/unlock design
  const handleLock = async () => {
    if (!design.is_locked) {
      if (!design.design_renders?.length) return toast.error("Generate design renders first");
      setLocking(true);
      try {
        await lock3DDesign(location.id);
        setDesign(prev => ({ ...prev, is_locked: true }));
        toast.success("Design locked — this location is finalized");
        if (onUpdate) onUpdate();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to lock"); }
      finally { setLocking(false); }
    } else {
      // Unlock
      setDesign(prev => ({ ...prev, is_locked: false }));
      toast.success("Design unlocked for editing");
    }
  };

  const handleDownload = (base64, name) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${base64}`;
    link.download = `${name}-${location.name}.png`;
    link.click();
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--color-gold-400)]" size={28} /></div>;
  }

  const TABS = [
    { id: "props", icon: Armchair, label: "Props" },
    { id: "room", icon: Building, label: "Room" },
    { id: "light", icon: Lightbulb, label: "Light" },
    { id: "refs", icon: Image, label: "Refs" },
  ];

  // Build layout object for SceneBuilder3D
  const layout3d = {
    room: design.room,
    props: design.props,
    characters: [],
    lighting: design.lighting,
  };

  return (
    <div className="h-full flex" data-testid="location-3d-designer">
      {/* Left: Controls */}
      <div className="w-56 border-r border-[var(--border-subtle)] flex flex-col bg-[var(--bg-surface)]">
        {/* Lock indicator */}
        {design.is_locked && (
          <div className="px-3 py-2 bg-[rgba(212,168,83,0.1)] border-b border-[rgba(212,168,83,0.2)] flex items-center gap-2">
            <Lock size={12} className="text-[var(--color-gold-400)]" />
            <span className="text-[10px] text-[var(--color-gold-400)] font-medium">Design Locked</span>
          </div>
        )}

        {/* Tab Nav */}
        <div className="flex border-b border-[var(--border-subtle)]">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 text-[10px] flex flex-col items-center gap-0.5 transition-all ${
                tab === t.id ? "text-[var(--color-gold-400)] border-b-2 border-[var(--color-gold-400)]" : "text-[var(--text-muted)]"
              }`}
              data-testid={`designer-tab-${t.id}`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3">
            {/* Props Tab */}
            {tab === "props" && (
              <div className="space-y-3">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                  {design.is_locked ? "Props (read only)" : "Click floor to place"}
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {PROP_CATALOG.map(p => (
                    <button key={p.type}
                      onClick={() => { setPlacePropType(p.type); if (!design.is_locked) addProp(p.type); }}
                      disabled={design.is_locked}
                      className={`text-[10px] py-2 px-1 rounded-lg border transition-all disabled:opacity-40 ${
                        placePropType === p.type
                          ? "bg-[rgba(212,168,83,0.15)] border-[rgba(212,168,83,0.4)] text-[var(--color-gold-400)]"
                          : "bg-[var(--bg-elevated)] border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                      data-testid={`add-prop-${p.type}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {design.props.length > 0 && (
                  <>
                    <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-3">In Scene ({design.props.length})</p>
                    <div className="space-y-1">
                      {design.props.map(p => (
                        <div key={p.id}
                          className={`flex items-center justify-between py-1.5 px-2 rounded-lg text-xs cursor-pointer hover:bg-[var(--bg-elevated)] ${
                            selectedObj?.id === p.id ? "bg-[rgba(212,168,83,0.1)] text-[var(--color-gold-400)]" : "text-[var(--text-primary)]"
                          }`}
                          onClick={() => setSelectedObj({ id: p.id, type: "prop" })}
                        >
                          <span className="truncate">{p.label}</span>
                          {!design.is_locked && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={(e) => { e.stopPropagation(); duplicateProp(p); }} className="text-[var(--text-disabled)] hover:text-[var(--text-primary)]"><Copy size={11} /></button>
                              <button onClick={(e) => { e.stopPropagation(); removeProp(p.id); }} className="text-[var(--text-disabled)] hover:text-[var(--color-error)]"><Trash2 size={11} /></button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Room Tab */}
            {tab === "room" && (
              <div className="space-y-3">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Type</p>
                <div className="flex gap-2">
                  {[{ id: "INT", label: "Interior", icon: Building }, { id: "EXT", label: "Exterior", icon: Mountain }].map(t => (
                    <button key={t.id}
                      onClick={() => !design.is_locked && setDesign(prev => ({ ...prev, location_type: t.id }))}
                      disabled={design.is_locked}
                      className={`flex-1 py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 transition-all ${
                        design.location_type === t.id
                          ? "bg-[var(--color-gold-500)] text-black font-medium"
                          : "bg-[var(--bg-elevated)] text-[var(--text-muted)] border border-[var(--border-subtle)]"
                      }`}
                      data-testid={`type-${t.id}`}
                    >
                      <t.icon size={12} />{t.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mt-2">Dimensions</p>
                {["width", "depth", "height"].map(dim => (
                  <div key={dim}>
                    <label className="text-[10px] text-[var(--text-muted)] capitalize">{dim} (m)</label>
                    <Input type="number" step="0.5" value={design.room?.[dim] || 8}
                      disabled={design.is_locked}
                      onChange={(e) => setDesign(prev => ({ ...prev, room: { ...prev.room, [dim]: Number(e.target.value) } }))}
                      className="input text-xs h-7 mt-0.5"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] text-[var(--text-muted)]">Wall Color</label>
                  <input type="color" value={design.room?.wall_color || "#e8e0d0"} disabled={design.is_locked}
                    onChange={(e) => setDesign(prev => ({ ...prev, room: { ...prev.room, wall_color: e.target.value } }))}
                    className="w-full h-7 rounded cursor-pointer mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] text-[var(--text-muted)]">Floor Color</label>
                  <input type="color" value={design.room?.floor_color || "#8b7355"} disabled={design.is_locked}
                    onChange={(e) => setDesign(prev => ({ ...prev, room: { ...prev.room, floor_color: e.target.value } }))}
                    className="w-full h-7 rounded cursor-pointer mt-0.5" />
                </div>
              </div>
            )}

            {/* Lighting Tab */}
            {tab === "light" && (
              <div className="space-y-3">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">3-Point Lighting</p>
                {["ambient", "key", "fill", "back"].map(light => (
                  <div key={light} className="p-2 rounded-lg bg-[var(--bg-elevated)] space-y-1.5">
                    <p className="text-[10px] font-medium text-[var(--text-primary)] capitalize">{light}</p>
                    <div className="flex gap-2 items-center">
                      <label className="text-[9px] text-[var(--text-muted)] w-8">Int</label>
                      <input type="range" min="0" max="2" step="0.1" disabled={design.is_locked}
                        value={design.lighting?.[light]?.intensity ?? 0.5}
                        onChange={(e) => setDesign(prev => ({
                          ...prev, lighting: { ...prev.lighting, [light]: { ...prev.lighting?.[light], intensity: Number(e.target.value) } }
                        }))}
                        className="flex-1 h-1 accent-[var(--color-gold-400)]" />
                      <span className="text-[9px] text-[var(--text-muted)] w-6 text-right">{(design.lighting?.[light]?.intensity ?? 0.5).toFixed(1)}</span>
                    </div>
                    <div className="flex gap-2 items-center">
                      <label className="text-[9px] text-[var(--text-muted)] w-8">Color</label>
                      <input type="color" value={design.lighting?.[light]?.color || "#ffffff"} disabled={design.is_locked}
                        onChange={(e) => setDesign(prev => ({
                          ...prev, lighting: { ...prev.lighting, [light]: { ...prev.lighting?.[light], color: e.target.value } }
                        }))}
                        className="w-6 h-5 rounded cursor-pointer border-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* References Tab */}
            {tab === "refs" && (
              <div className="space-y-3">
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Reference Images</p>
                <p className="text-[9px] text-[var(--text-disabled)]">Upload images for AI to use as design inspiration</p>
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="btn-secondary w-full text-xs h-8 justify-center" size="sm" data-testid="upload-ref-btn">
                  {uploading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                  Upload Reference
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadRef} />
                {design.reference_uploads?.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {design.reference_uploads.map((ref, i) => (
                      <div key={ref.id || i} className="rounded-lg overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
                        {ref.image_data && (
                          <img src={`data:image/png;base64,${ref.image_data}`} alt={ref.label} className="w-full h-20 object-cover" />
                        )}
                        <div className="px-2 py-1">
                          <span className="text-[10px] text-[var(--text-muted)]">{ref.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-[var(--border-subtle)] space-y-2">
          <Button onClick={handleSave} disabled={saving} className="btn-secondary w-full text-xs h-8 justify-center" size="sm" data-testid="save-3d-design-btn">
            {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
            Save Design
          </Button>
          <Button onClick={handleLock} disabled={locking}
            className={`w-full text-xs h-8 justify-center ${design.is_locked ? "btn-ghost border border-[var(--border-default)]" : "btn-primary"}`} size="sm" data-testid="lock-design-btn">
            {design.is_locked ? <><Unlock size={12} className="mr-1" /> Unlock</> : <><Lock size={12} className="mr-1" /> Lock Design</>}
          </Button>
        </div>
      </div>

      {/* Right: 3D Viewport + Generate */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-surface)] border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${
              design.location_type === "INT"
                ? "bg-amber-500/10 text-amber-400"
                : "bg-sky-500/10 text-sky-400"
            }`}>{design.location_type}</span>
            <span className="text-xs text-[var(--text-primary)] font-medium">{location.name}</span>
            <span className="text-[10px] text-[var(--text-disabled)]">
              {design.is_locked ? "Locked" : "Click to select | Drag to move | Click floor to place"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Input value={design.theme || ""}
              onChange={(e) => setDesign(prev => ({ ...prev, theme: e.target.value }))}
              placeholder="Theme (e.g. middle-class Indian household)..."
              className="input text-xs h-8 w-56" data-testid="theme-input" />
            <Button onClick={handleGenerate} disabled={generating}
              className="btn-primary text-xs h-8" size="sm" data-testid="generate-design-btn">
              {generating ? <Loader2 size={12} className="animate-spin mr-1" /> : <Palette size={12} className="mr-1" />}
              {generating ? "Generating..." : "Generate Design"}
            </Button>
          </div>
        </div>

        {/* 3D Viewport */}
        <div className="flex-1 relative">
          <SceneBuilder3D
            layout={layout3d}
            onLayoutChange={design.is_locked ? undefined : handleLayoutChange}
            onSelectObject={setSelectedObj}
            selectedId={selectedObj?.id}
            onFloorClick={design.is_locked ? undefined : handleFloorClick}
          />

          {/* Generating overlay */}
          {generating && (
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin text-[var(--color-gold-400)] mx-auto mb-3" />
                <p className="text-sm text-white/80 font-medium">Generating multi-angle renders...</p>
                <p className="text-xs text-white/40 mt-1">Frame chaining: each angle references the previous for consistency</p>
                <p className="text-xs text-white/30 mt-1">This takes 2-3 minutes (4 sequential generations)</p>
              </div>
            </div>
          )}
        </div>

        {/* Design Renders Gallery */}
        {design.design_renders?.length > 0 && (
          <div className="border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Design Renders ({design.design_renders.length} angles)</p>
              {design.is_locked && (
                <span className="text-[9px] text-[var(--color-gold-400)] flex items-center gap-1">
                  <Lock size={10} /> Locked
                </span>
              )}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {design.design_renders.map((render, i) => (
                <div key={render.angle || i} className="flex-shrink-0 w-40 group cursor-pointer"
                  onClick={() => setShowLightbox(render)}>
                  <img src={`data:image/png;base64,${render.image_data}`}
                    alt={render.label || render.angle}
                    className="w-full h-24 object-cover rounded-lg border border-[var(--border-subtle)] group-hover:border-[rgba(212,168,83,0.5)] transition-all" />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-[var(--text-muted)]">{render.label || render.angle}</span>
                    <button onClick={(e) => { e.stopPropagation(); handleDownload(render.image_data, render.angle); }}
                      className="text-[var(--text-disabled)] hover:text-[var(--text-primary)]"><Download size={10} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!showLightbox} onOpenChange={() => setShowLightbox(null)}>
        <DialogContent className="bg-black/95 border-[var(--border-subtle)] max-w-3xl p-2">
          {showLightbox && (
            <div className="relative">
              <img src={`data:image/png;base64,${showLightbox.image_data}`}
                alt={showLightbox.label} className="w-full rounded-lg" />
              <div className="absolute top-2 right-2 flex gap-2">
                <button onClick={() => handleDownload(showLightbox.image_data, showLightbox.angle || "render")}
                  className="p-2 rounded-lg bg-black/60 text-white/70 hover:text-white"><Download size={16} /></button>
              </div>
              <p className="text-center text-sm text-white/60 mt-2">{showLightbox.label || showLightbox.angle} — {location.name}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
